/**
 * Catálogo das camadas de mapa: textos para o cidadão, arquivos usados,
 * estilos padrão, controles de estilo exibidos no painel e predefinições.
 *
 * Os padrões foram pensados para contraste sobre imagem de satélite
 * (fundo escuro/verde, nuvens brancas). Laranja fica reservado para fogo.
 */
import type { DataLayerId, LayerStyle, PresetId, RegionLayerId, StyleKey, VectorLayerId } from './types';

// ---------------------------------------------------------------------------
// Campos de estilo: tipo, faixa e código curto usado no link (URL)
// ---------------------------------------------------------------------------

export type StyleFieldSpec =
  | { kind: 'color'; code: string }
  | { kind: 'boolean'; code: string }
  /** `scale`: o valor é gravado na URL como inteiro `round(v × scale)`. */
  | { kind: 'number'; code: string; min: number; max: number; step: number; scale: number };

/** Especificação de cada campo de `LayerStyle`. Os códigos são únicos (1 letra). */
export const STYLE_FIELDS: Record<StyleKey, StyleFieldSpec> = {
  strokeColor: { kind: 'color', code: 'c' },
  strokeWidth: { kind: 'number', code: 'w', min: 0.2, max: 6, step: 0.1, scale: 10 },
  fill: { kind: 'boolean', code: 'f' },
  fillColor: { kind: 'color', code: 'g' },
  fillOpacity: { kind: 'number', code: 'q', min: 0, max: 1, step: 0.05, scale: 100 },
  opacity: { kind: 'number', code: 'o', min: 0.1, max: 1, step: 0.05, scale: 100 },
  labels: { kind: 'boolean', code: 'l' },
  labelSize: { kind: 'number', code: 's', min: 8, max: 24, step: 1, scale: 1 },
  labelColor: { kind: 'color', code: 'k' },
  points: { kind: 'boolean', code: 'p' },
  density: { kind: 'number', code: 'd', min: -1, max: 1, step: 1, scale: 1 },
  altColor: { kind: 'color', code: 'a' },
  altOn: { kind: 'boolean', code: 'b' },
};

// ---------------------------------------------------------------------------
// Camadas
// ---------------------------------------------------------------------------

export interface StyleControl {
  key: StyleKey;
  label: string;
  /** Campo booleano que precisa estar ligado para este controle fazer efeito. */
  dependsOn?: StyleKey;
}

export interface LegendItem {
  /** Campo de estilo que dá a cor do item. */
  colorKey: StyleKey;
  label: string;
  hint?: string;
}

/** Grupos do painel, na ordem de exibição. */
export type LayerGroupId = 'referencia' | 'regioes' | 'protegidas' | 'infra';

export interface LayerGroupDef {
  id: LayerGroupId;
  title: string;
  /** Explicação curta mostrada quando o grupo está aberto (opcional). */
  hint?: string;
  defaultOpen: boolean;
}

export const LAYER_GROUPS: readonly LayerGroupDef[] = [
  { id: 'referencia', title: 'Referência', defaultOpen: true },
  {
    id: 'regioes',
    title: 'Sub-regiões',
    hint: 'Divisões oficiais que agrupam municípios vizinhos (IBGE e Governo do Pará).',
    defaultOpen: false,
  },
  { id: 'protegidas', title: 'Áreas protegidas', defaultOpen: true },
  { id: 'infra', title: 'Rios e rodovias', defaultOpen: true },
];

export interface VectorLayerDef {
  id: VectorLayerId;
  group: LayerGroupId;
  /** Código de 1 letra usado no link compartilhável. */
  code: string;
  title: string;
  description: string;
  /** Arquivos (ids do manifest) usados pela camada. */
  data: DataLayerId[];
  defaultVisible: boolean;
  defaultStyle: LayerStyle;
  /** Controles do editor de estilo, na ordem de exibição. */
  controls: StyleControl[];
  legend?: LegendItem[];
}

/** Valores de base para os campos que uma camada não usa. */
const BASE_STYLE: LayerStyle = {
  strokeColor: '#ffffff',
  strokeWidth: 1,
  fill: false,
  fillColor: '#ffffff',
  fillOpacity: 0.2,
  opacity: 1,
  labels: false,
  labelSize: 12,
  labelColor: '#ffffff',
  points: false,
  density: 0,
  altColor: '#ffffff',
  altOn: false,
};

const style = (patch: Partial<LayerStyle>): LayerStyle => ({ ...BASE_STYLE, ...patch });

const OPACITY: StyleControl = { key: 'opacity', label: 'Opacidade geral' };

/** Controles comuns às camadas de sub-regiões. */
const REGION_CONTROLS: StyleControl[] = [
  { key: 'strokeColor', label: 'Cor das divisas' },
  { key: 'strokeWidth', label: 'Espessura das divisas' },
  { key: 'labels', label: 'Mostrar nomes' },
  { key: 'labelSize', label: 'Tamanho do texto', dependsOn: 'labels' },
  { key: 'labelColor', label: 'Cor do texto', dependsOn: 'labels' },
  { key: 'fill', label: 'Preencher áreas' },
  { key: 'fillColor', label: 'Cor do preenchimento', dependsOn: 'fill' },
  { key: 'fillOpacity', label: 'Opacidade do preenchimento', dependsOn: 'fill' },
  OPACITY,
];

function regionLayer(
  id: RegionLayerId,
  code: string,
  title: string,
  description: string,
  color: string,
  strokeWidth: number,
  /** Nomes ligados de início só onde não repetem o nome de uma cidade (ex.: "Sudeste Paraense"). */
  labels: boolean,
): VectorLayerDef {
  return {
    id,
    group: 'regioes',
    code,
    title,
    description,
    data: [id],
    defaultVisible: false,
    defaultStyle: style({
      strokeColor: color,
      strokeWidth,
      opacity: 0.9,
      fill: false,
      fillColor: color,
      fillOpacity: 0.15,
      labels,
      labelSize: strokeWidth >= 1.5 ? 12 : 11,
      // Na cor da divisa, para não confundir com os nomes das cidades (brancos).
      labelColor: color,
    }),
    controls: REGION_CONTROLS,
  };
}

export const VECTOR_LAYERS: readonly VectorLayerDef[] = [
  {
    id: 'sedes',
    group: 'referencia',
    code: 'c',
    title: 'Nomes das cidades',
    description: 'Capitais e cidades maiores primeiro; mais nomes aparecem ao aproximar.',
    data: ['sedes'],
    defaultVisible: true,
    defaultStyle: style({
      labels: true,
      labelSize: 12,
      labelColor: '#ffffff',
      points: true,
      strokeColor: '#fde047',
      density: 0,
    }),
    controls: [
      { key: 'labels', label: 'Mostrar nomes' },
      { key: 'labelSize', label: 'Tamanho do texto', dependsOn: 'labels' },
      { key: 'labelColor', label: 'Cor do texto', dependsOn: 'labels' },
      { key: 'density', label: 'Quantidade de nomes', dependsOn: 'labels' },
      { key: 'points', label: 'Mostrar ponto da cidade' },
      { key: 'strokeColor', label: 'Cor do ponto', dependsOn: 'points' },
      OPACITY,
    ],
  },
  {
    id: 'municipios',
    group: 'referencia',
    code: 'm',
    title: 'Municípios',
    description: 'Limites de todos os municípios da Amazônia Legal (IBGE).',
    data: ['municipios'],
    defaultVisible: false,
    defaultStyle: style({
      strokeColor: '#e2e8f0',
      strokeWidth: 0.6,
      opacity: 0.7,
      fill: false,
      fillColor: '#94a3b8',
      fillOpacity: 0.2,
    }),
    controls: [
      { key: 'strokeColor', label: 'Cor da linha' },
      { key: 'strokeWidth', label: 'Espessura da linha' },
      { key: 'fill', label: 'Preencher áreas' },
      { key: 'fillColor', label: 'Cor do preenchimento', dependsOn: 'fill' },
      { key: 'fillOpacity', label: 'Opacidade do preenchimento', dependsOn: 'fill' },
      OPACITY,
    ],
  },
  {
    id: 'estados',
    group: 'referencia',
    code: 'e',
    title: 'Estados e Amazônia Legal',
    description: 'Divisas estaduais e o limite da Amazônia Legal.',
    data: ['estados', 'amazonia_legal'],
    defaultVisible: false,
    defaultStyle: style({
      strokeColor: '#ffffff',
      strokeWidth: 1.5,
      opacity: 0.9,
      altOn: true,
      altColor: '#fde047',
      labels: false,
      labelSize: 14,
      labelColor: '#ffffff',
    }),
    controls: [
      { key: 'strokeColor', label: 'Cor das divisas' },
      { key: 'strokeWidth', label: 'Espessura das divisas' },
      { key: 'altOn', label: 'Mostrar limite da Amazônia Legal' },
      { key: 'altColor', label: 'Cor do limite da Amazônia Legal', dependsOn: 'altOn' },
      { key: 'labels', label: 'Mostrar siglas dos estados' },
      { key: 'labelSize', label: 'Tamanho das siglas', dependsOn: 'labels' },
      { key: 'labelColor', label: 'Cor das siglas', dependsOn: 'labels' },
      OPACITY,
    ],
  },
  regionLayer(
    'mesorregioes',
    'o',
    'Mesorregiões',
    'Divisão clássica do IBGE (1990), como Sudeste Paraense, Sudoeste Paraense e Norte Mato-grossense.',
    '#e9d5ff',
    1.8,
    true,
  ),
  regionLayer(
    'microrregioes',
    'i',
    'Microrregiões',
    'Subdivisões das mesorregiões (IBGE, 1990).',
    '#c4b5fd',
    1,
    false,
  ),
  regionLayer(
    'regioes_intermediarias',
    'n',
    'Regiões intermediárias',
    'Divisão atual do IBGE (2017), organizada em torno de cidades polo como Santarém, Marabá e Sinop.',
    '#5eead4',
    1.8,
    false,
  ),
  regionLayer(
    'regioes_imediatas',
    'd',
    'Regiões imediatas',
    'Subdivisões das regiões intermediárias (IBGE, 2017).',
    '#99f6e4',
    1,
    false,
  ),
  regionLayer(
    'regioes_integracao_pa',
    'a',
    'Regiões de Integração do Pará',
    'As 12 regiões de planejamento do Governo do Pará, como Tapajós, Xingu, Carajás e Baixo Amazonas.',
    '#fda4af',
    1.8,
    true,
  ),
  {
    id: 'terras_indigenas',
    group: 'protegidas',
    code: 't',
    title: 'Terras indígenas',
    description: 'Terras indígenas reconhecidas ou em estudo (FUNAI).',
    data: ['terras_indigenas'],
    defaultVisible: false,
    defaultStyle: style({
      strokeColor: '#f472b6',
      strokeWidth: 1.2,
      fill: true,
      fillColor: '#ec4899',
      fillOpacity: 0.18,
      labels: false,
      labelSize: 11,
      labelColor: '#fce7f3',
    }),
    controls: [
      { key: 'strokeColor', label: 'Cor do contorno' },
      { key: 'strokeWidth', label: 'Espessura do contorno' },
      { key: 'fill', label: 'Preencher áreas' },
      { key: 'fillColor', label: 'Cor do preenchimento', dependsOn: 'fill' },
      { key: 'fillOpacity', label: 'Opacidade do preenchimento', dependsOn: 'fill' },
      { key: 'labels', label: 'Mostrar nomes' },
      { key: 'labelSize', label: 'Tamanho do texto', dependsOn: 'labels' },
      { key: 'labelColor', label: 'Cor do texto', dependsOn: 'labels' },
      OPACITY,
    ],
  },
  {
    id: 'unidades_conservacao',
    group: 'protegidas',
    code: 'u',
    title: 'Unidades de conservação',
    description: 'Parques, reservas e florestas protegidas, por tipo de proteção.',
    data: ['unidades_conservacao'],
    defaultVisible: false,
    defaultStyle: style({
      strokeColor: '#4ade80',
      altColor: '#a78bfa',
      strokeWidth: 1.2,
      fill: true,
      fillOpacity: 0.16,
      labels: false,
      labelSize: 11,
      labelColor: '#ecfdf5',
    }),
    controls: [
      { key: 'strokeColor', label: 'Cor — Proteção Integral' },
      { key: 'altColor', label: 'Cor — Uso Sustentável' },
      { key: 'strokeWidth', label: 'Espessura do contorno' },
      { key: 'fill', label: 'Preencher áreas' },
      { key: 'fillOpacity', label: 'Opacidade do preenchimento', dependsOn: 'fill' },
      { key: 'labels', label: 'Mostrar nomes' },
      { key: 'labelSize', label: 'Tamanho do texto', dependsOn: 'labels' },
      { key: 'labelColor', label: 'Cor do texto', dependsOn: 'labels' },
      OPACITY,
    ],
    legend: [
      {
        colorKey: 'strokeColor',
        label: 'Proteção Integral',
        hint: 'Natureza preservada; só uso indireto (ex.: parques, reservas biológicas).',
      },
      {
        colorKey: 'altColor',
        label: 'Uso Sustentável',
        hint: 'Permite uso controlado dos recursos (ex.: reservas extrativistas, florestas nacionais).',
      },
    ],
  },
  {
    id: 'rios',
    group: 'infra',
    code: 'r',
    title: 'Rios',
    description: 'Rios principais; a espessura indica a importância do rio.',
    data: ['rios'],
    defaultVisible: false,
    defaultStyle: style({
      strokeColor: '#7dd3fc',
      strokeWidth: 2,
      opacity: 0.9,
      labels: false,
      labelSize: 11,
      labelColor: '#e0f2fe',
    }),
    controls: [
      { key: 'strokeColor', label: 'Cor dos rios' },
      { key: 'strokeWidth', label: 'Espessura (rios principais)' },
      { key: 'labels', label: 'Mostrar nomes dos rios' },
      { key: 'labelSize', label: 'Tamanho do texto', dependsOn: 'labels' },
      { key: 'labelColor', label: 'Cor do texto', dependsOn: 'labels' },
      OPACITY,
    ],
  },
  {
    id: 'rodovias',
    group: 'infra',
    code: 'h',
    title: 'Rodovias',
    description: 'Principais rodovias federais (BR).',
    data: ['rodovias'],
    defaultVisible: false,
    defaultStyle: style({
      strokeColor: '#f59e0b',
      strokeWidth: 1.4,
      opacity: 0.95,
      labels: false,
      labelSize: 10,
      labelColor: '#ffffff',
      altColor: '#b91c1c',
    }),
    controls: [
      { key: 'strokeColor', label: 'Cor das rodovias' },
      { key: 'strokeWidth', label: 'Espessura' },
      { key: 'labels', label: 'Mostrar número (ex.: BR-163)' },
      { key: 'labelSize', label: 'Tamanho do número', dependsOn: 'labels' },
      { key: 'altColor', label: 'Cor da placa', dependsOn: 'labels' },
      { key: 'labelColor', label: 'Cor do número', dependsOn: 'labels' },
      OPACITY,
    ],
  },
];

const BY_ID = new Map(VECTOR_LAYERS.map((l) => [l.id, l]));
const BY_CODE = new Map(VECTOR_LAYERS.map((l) => [l.code, l]));

export function getLayerDef(id: VectorLayerId): VectorLayerDef {
  const def = BY_ID.get(id);
  if (!def) throw new Error(`Camada vetorial desconhecida: ${id}`);
  return def;
}

export function getLayerDefByCode(code: string): VectorLayerDef | undefined {
  return BY_CODE.get(code);
}

/** Campos de estilo que a camada expõe no painel (os únicos gravados na URL). */
export function editableKeys(id: VectorLayerId): StyleKey[] {
  return getLayerDef(id).controls.map((c) => c.key);
}

export function defaultVisibility(): Record<VectorLayerId, boolean> {
  return Object.fromEntries(VECTOR_LAYERS.map((l) => [l.id, l.defaultVisible])) as Record<VectorLayerId, boolean>;
}

// ---------------------------------------------------------------------------
// Predefinições globais
// ---------------------------------------------------------------------------

export interface PresetDef {
  id: PresetId;
  /** Código de 1 letra usado no link. */
  code: string;
  label: string;
  description: string;
  /** Transforma o estilo padrão de uma camada. */
  apply: (id: VectorLayerId, s: LayerStyle) => LayerStyle;
}

const round1 = (v: number) => Math.round(v * 10) / 10;
const round2 = (v: number) => Math.round(v * 100) / 100;
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export const PRESETS: readonly PresetDef[] = [
  {
    id: 'default',
    code: 'd',
    label: 'Normal',
    description: 'Equilíbrio entre a imagem do satélite e as linhas do mapa.',
    apply: (_id, s) => s,
  },
  {
    id: 'contrast',
    code: 'a',
    label: 'Alto contraste',
    description: 'Linhas mais grossas e textos maiores, mais fáceis de ler.',
    apply: (id, s) => {
      const out: LayerStyle = {
        ...s,
        strokeWidth: round1(clamp(s.strokeWidth * 1.6, 0.2, 6)),
        opacity: 1,
        labelSize: clamp(s.labelSize + 2, 8, 24),
        fillOpacity: round2(clamp(s.fillOpacity * 1.5, 0, 1)),
      };
      if (id === 'municipios') out.strokeColor = '#ffffff';
      if (id === 'estados') out.altColor = '#facc15';
      if (id === 'rios') out.strokeColor = '#38bdf8';
      if (id === 'sedes') out.labelColor = '#ffffff';
      return out;
    },
  },
  {
    id: 'subtle',
    code: 's',
    label: 'Discreto',
    description: 'Linhas finas e transparentes, para ver melhor a imagem.',
    apply: (id, s) => {
      const out: LayerStyle = {
        ...s,
        strokeWidth: round1(clamp(s.strokeWidth * 0.7, 0.2, 6)),
        opacity: round2(clamp(s.opacity * 0.6, 0.3, 1)),
        labelSize: clamp(s.labelSize - 1, 8, 24),
        fillOpacity: round2(clamp(s.fillOpacity * 0.6, 0, 1)),
      };
      if (id === 'sedes') {
        out.density = -1;
        out.opacity = 0.85;
      }
      return out;
    },
  },
];

/** Predefinição inicial: linhas finas e transparentes, para ver melhor a imagem. */
export const DEFAULT_PRESET: PresetId = 'subtle';

export function getPreset(id: PresetId): PresetDef {
  return PRESETS.find((p) => p.id === id) ?? PRESETS[0];
}

export function getPresetByCode(code: string): PresetDef | undefined {
  return PRESETS.find((p) => p.code === code);
}

/** Nomes das UFs da Amazônia Legal (para popups). */
export const UF_NAMES: Record<string, string> = {
  AC: 'Acre',
  AM: 'Amazonas',
  AP: 'Amapá',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  PA: 'Pará',
  RO: 'Rondônia',
  RR: 'Roraima',
  TO: 'Tocantins',
};
